import SuperadminIndicator from "../../../components/auth/SuperadminIndicator.jsx";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import Button from "../../../components/Button.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleCheck, faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";

export function IntegrationAdminMetadata({integrationId}) {
    const {t} = useTranslation();
    const copyIntegrationId = () => navigator.clipboard.writeText(integrationId)
        .then(() => toast.success(t('copy-to-clipboard-success')))
        .catch(() => toast.error(t('copy-to-clipboard-failed')));

    return <div className={'app-surface relative rounded-xl border border-violet-200 bg-violet-50/70 px-4 py-3 pr-12 text-sm text-violet-950 shadow-sm dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-100'}>
        <SuperadminIndicator className={'absolute right-3 top-3'}/>
        <div className={'text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300'}>ID INTEGRACE</div>
        <div className={'mt-1 flex min-w-0 items-center'}>
            <button
                type={'button'}
                className={'min-w-0 break-all text-left font-mono text-sm hover:text-violet-700 hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:hover:text-violet-200'}
                title={t('copy-to-clipboard')}
                onClick={copyIntegrationId}
            >
                {integrationId}
            </button>
        </div>
    </div>;
}

export function IntegrationSection({title, description, actions, children, className = ''}) {
    return <section className={`app-surface rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 ${className}`}>
        {(title || description || actions) && <div className={'mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'}>
            <div>
                {title && <h2 className={'text-lg font-semibold text-slate-900 dark:text-white'}>{title}</h2>}
                {description && <p className={'mt-1 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300'}>{description}</p>}
            </div>
            {actions && <div className={'shrink-0'}>{actions}</div>}
        </div>}
        {children}
    </section>;
}

export function IntegrationActions({hint, children}) {
    return <div className={'flex flex-nowrap items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-700'}>
        <div className={'min-w-0 text-xs leading-5 text-slate-500 dark:text-slate-400'}>{hint}</div>
        <div className={'flex shrink-0 flex-nowrap justify-end gap-2'}>{children}</div>
    </div>;
}

export function IntegrationAddButton({children, onClick}) {
    return <button
        type={'button'}
        className={'integration-add-button min-h-10 rounded-lg border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-semibold leading-5 text-white shadow-sm transition-colors hover:border-emerald-700 hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2'}
        onClick={onClick}
    >
        {children}
    </button>;
}

export function OAuthConnectionStatus({connected, action, connectedDescription = 'Datixo má uložené oprávnění. Obnovení připojení nezmění vybrané zdroje.'}) {
    return <div className={`app-surface flex flex-col gap-4 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${connected
        ? 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100'
        : 'border-red-200 bg-red-50 text-datixo-negative dark:border-red-800 dark:bg-red-950/30 text-datixo-negative'}`}>
        <div className={'flex items-start gap-3'}>
            <FontAwesomeIcon
                icon={connected ? faCircleCheck : faTriangleExclamation}
                className={`mt-0.5 text-lg ${connected ? 'text-emerald-600 dark:text-emerald-300' : 'text-datixo-negative'}`}
            />
            <div>
                <div className={'text-sm font-semibold'}>{connected ? 'OAuth připojení je aktivní' : 'OAuth připojení vyžaduje obnovení'}</div>
                <div className={`mt-1 text-xs leading-5 ${connected ? 'text-emerald-800 dark:text-emerald-200' : 'text-datixo-negative'}`}>
                    {connected ? connectedDescription : 'Znovu se přihlaste u poskytovatele, aby bylo možné pokračovat ve stahování.'}
                </div>
            </div>
        </div>
        {action && <div className={'shrink-0'}>{action}</div>}
    </div>;
}

export function IntegrationConnectButton({logo, logoAlt, icon, onClick, label = 'Připojit'}) {
    return <Button
        variant={'outline-primary'}
        className={'integration-connect-button'}
        onClick={onClick}
    >
        {logo ? <img src={logo} alt={logoAlt} className={'h-5 w-5'}/> : <span className={'flex h-5 w-5 items-center justify-center'}>{icon}</span>}
        <span>{label}</span>
    </Button>;
}
